// ============================================================
// funcionalidades/sync.jsx — qué ve el comerciante del respaldo en la nube.
//
// ESTO ES SOLO EL FRENTE. Todavía no sube nada: define las palabras, los
// estados y las dos pantallas, para poder verlas y corregirlas antes de
// construir la cola de verdad. Lo que SÍ está terminado y probado son las
// funciones puras de abajo (resumir, textoDe, haceCuanto), porque de ellas
// depende qué dice la pantalla.
//
// LA REGLA QUE MANDA SOBRE TODO LO DEMÁS: vender NUNCA espera. Nada de
// aquí bloquea una venta, ni abre un aviso encima, ni pide reintentar. Si la
// nube está caída, el comerciante no se tiene que enterar a media venta: lo
// ve después, cuando él quiera. Una venta perdida por esperar al servidor es
// peor que un respaldo tarde.
//
// Y NO SE DICE "SINCRONIZAR". Nadie en un tianguis usa esa palabra. Lo que
// el comerciante se pregunta es "¿si se me pierde el teléfono, pierdo mis
// ventas?" y "¿mi vendedor y yo vemos lo mismo?". Las palabras de aquí
// contestan eso: "en la nube", "faltan 3".
// ============================================================

import React, { useState } from "react";
import { Cloud, CloudOff, CloudUpload, AlertTriangle, RefreshCw, Check } from "lucide-react";
import { COL, tarjeta } from "./tema.jsx";
import { money } from "./contrato.jsx";
import { bitacora } from "./bitacora.jsx";

// Mientras la cola de verdad no exista, esto llena las pantallas con una
// cola de mentiras para poder verlas. Se apaga de un solo lugar.
//   true  = enseña la píldora con datos inventados, para revisar el diseño
//   false = la píldora solo aparece si hay servidor configurado
export const DEMO = true;

// ------------------------------------------------------------
// Los estados. Son cinco y ni uno más: cada estado de más es una pantalla
// que hay que diseñar, probar y que el comerciante tiene que entender.
//
// "apagado" no está en la lista a propósito: cuando no hay servidor —el plan
// Puesto, que vive entero en el teléfono— la píldora NO SE PINTA. Decirle
// "sin internet" a quien nunca quiso nube es ruido, y lo entrena a ignorar
// el aviso para cuando sí importe.
// ------------------------------------------------------------
export const ESTADOS = {
  al_dia: {
    icono: Cloud, color: COL.billete, fondo: "#E4F0E8",
    corto: () => "En la nube",
    titulo: () => "Todo está en la nube",
    detalle: () => "Si se te pierde el teléfono, no pierdes nada.",
  },
  subiendo: {
    icono: CloudUpload, color: COL.tinta, fondo: COL.crema,
    corto: (n) => `Guardando ${n}`,
    titulo: (n) => `Guardando ${n} ${n === 1 ? "cosa" : "cosas"}`,
    detalle: () => "Puedes seguir vendiendo mientras.",
  },
  pendiente: {
    icono: CloudUpload, color: COL.oro, fondo: "#F7EFD9",
    corto: (n) => `Faltan ${n}`,
    titulo: (n) => `Faltan ${n} por guardar`,
    detalle: () => "Se guardan solas en cuanto haya con qué.",
  },
  sin_internet: {
    icono: CloudOff, color: COL.kraft, fondo: COL.crema,
    corto: (n) => `Sin internet · ${n}`,
    titulo: (n) => `Sin internet · ${n} ${n === 1 ? "espera" : "esperan"}`,
    // Esto NO es un error y así se dice. En el tianguis es lo normal.
    detalle: () => "Nada se perdió. Están en el teléfono esperando señal.",
  },
  atorado: {
    icono: AlertTriangle, color: COL.chile, fondo: "#F7E4E0",
    corto: (n) => `No se pudo · ${n}`,
    titulo: (n) => `No se pudieron guardar ${n}`,
    // El único estado que pide que alguien haga algo.
    detalle: () => "Siguen en el teléfono. Avísale a quien te ayuda con esto.",
  },
};

// ------------------------------------------------------------
// De qué tamaño es el problema, en una palabra.
//
// Función pura a propósito: es la que decide qué ve el comerciante, así que
// tiene que poder probarse sin pantalla, sin red y sin servidor.
//
// El orden de las preguntas importa. Primero "no se pudo", porque un error
// con cola pendiente es lo único que amerita color rojo. Después "sin
// internet", que no es falla. Al final lo normal.
// ------------------------------------------------------------
export function resumir({ cola = [], subiendo = false, hayInternet = true, fallas = 0, hayServidor = true } = {}) {
  if (!hayServidor) return { estado: null, cuantos: 0 };   // la píldora no se pinta
  const cuantos = cola.length;
  if (cuantos === 0) return { estado: "al_dia", cuantos: 0 };
  if (subiendo) return { estado: "subiendo", cuantos };
  // Tres tropiezos seguidos ya no son mala señal, es algo roto. Antes de
  // eso no se le pinta rojo al comerciante por un bache del camión.
  if (fallas >= 3) return { estado: "atorado", cuantos };
  if (!hayInternet) return { estado: "sin_internet", cuantos };
  return { estado: "pendiente", cuantos };
}

// ------------------------------------------------------------
// Qué dice un renglón de la cola, en palabras de comerciante.
//
// La cola guarda movimientos del libro, con motivos como "entrada" o
// "entrega". Esas son palabras del esquema, no del puesto. Aquí se traducen,
// una sola vez, para que no se le escapen a ninguna pantalla.
// ------------------------------------------------------------
const PALABRAS = {
  venta:      (m) => `Venta${m.monto != null ? " de " + money(m.monto) : ""}`,
  entrada:    (m) => `Entró mercancía${m.piezas ? " · " + m.piezas : ""}`,
  apartado:   () => "Apartado",
  entrega:    () => "Se lo llevó",
  devolucion: () => "Devolución",
  ajuste:     () => "Corrección de existencia",
  merma:      () => "Merma",
  inicial:    () => "Existencia de arranque",
  producto:   (m) => `Producto nuevo${m.que ? " · " + m.que : ""}`,
};

export function textoDe(item) {
  if (!item) return "Algo sin identificar";
  const f = PALABRAS[item.motivo];
  if (f) return f(item);
  // Si llega un motivo que nadie tradujo, se dice algo honesto y queda
  // apuntado, en vez de pintarle al comerciante una palabra del esquema.
  bitacora.aviso("sync", "motivo sin traducir", { motivo: item.motivo });
  return "Un movimiento del inventario";
}

// Hace cuánto, redondeado como lo diría una persona.
export function haceCuanto(cuando, ahora = Date.now()) {
  if (!cuando) return "nunca";
  const seg = Math.floor((ahora - new Date(cuando).getTime()) / 1000);
  if (seg < 0) return "hace un momento";          // el reloj del teléfono anda adelantado
  if (seg < 45) return "hace un momento";
  const min = Math.round(seg / 60);
  if (min < 60) return `hace ${min} ${min === 1 ? "minuto" : "minutos"}`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `hace ${hrs} ${hrs === 1 ? "hora" : "horas"}`;
  const dias = Math.round(hrs / 24);
  return `hace ${dias} ${dias === 1 ? "día" : "días"}`;
}

// ------------------------------------------------------------
// La píldora del encabezado. Chiquita, se toca y abre el detalle.
//
// No es un botón de "subir ahora": es un semáforo. El comerciante no tiene
// que apretar nada para que las cosas se guarden.
// ------------------------------------------------------------
export function PildoraSync({ resumen, onAbrir }) {
  const { estado, cuantos } = resumen || {};
  if (!estado) return null;
  const E = ESTADOS[estado];
  if (!E) return null;
  const Icono = E.icono;

  return (
    <button onClick={onAbrir} aria-label={E.titulo(cuantos)}
      style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
        border: "none", borderRadius: 11, padding: "6px 10px", fontFamily: "inherit",
        background: E.fondo, color: E.color, fontSize: 11.5, fontWeight: 800, flexShrink: 0 }}>
      <Icono size={14} />
      {E.corto(cuantos)}
    </button>
  );
}

// ------------------------------------------------------------
// El detalle. Se llega aquí tocando la píldora.
// Misma forma que la Bitácora: tarjeta de estado arriba, lista abajo.
// ------------------------------------------------------------
export function PantallaSync({ resumen, cola = [], ultima, onIntentar, razon }) {
  const [intentando, setIntentando] = useState(false);
  const { estado, cuantos } = resumen || { estado: "al_dia", cuantos: 0 };
  const E = ESTADOS[estado] || ESTADOS.al_dia;
  const Icono = E.icono;

  const intentar = async () => {
    setIntentando(true);
    try { await onIntentar?.(); } finally { setIntentando(false); }
  };

  return (
    <div style={{ padding: "12px 14px 20px" }}>
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 10 }}>Respaldo en la nube</div>

      <div style={{ ...tarjeta, padding: 14, marginBottom: 12, background: E.fondo }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <Icono size={22} color={E.color} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{E.titulo(cuantos)}</div>
            <div style={{ fontSize: 12.5, color: COL.kraft, fontWeight: 600, marginTop: 2 }}>
              {E.detalle(cuantos)}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${E.color}`,
          fontSize: 12, fontWeight: 700, color: COL.kraft }}>
          Última vez que se guardó: {haceCuanto(ultima)}
        </div>

        {/* El motivo técnico va al final y en chico: sirve a quien lo arregla,
            no al comerciante. Pero no se esconde: esconderlo obliga a pedir
            capturas de la consola, que es justo lo que no queremos. */}
        {estado === "atorado" && razon && (
          <div style={{ marginTop: 8, fontSize: 11, color: COL.chile, fontWeight: 700, lineHeight: 1.35 }}>
            {razon}
          </div>
        )}
      </div>

      {cuantos > 0 && (
        <>
          <button onClick={intentar} disabled={intentando}
            style={{ width: "100%", padding: 13, borderRadius: 13, cursor: intentando ? "default" : "pointer",
              fontFamily: "inherit", border: "none", background: intentando ? COL.kraft : COL.tinta,
              color: COL.papel, fontSize: 14, fontWeight: 800, display: "flex",
              alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 12 }}>
            <RefreshCw size={17} />
            {intentando ? "Guardando..." : "Intentar ahora"}
          </button>

          <div style={{ ...tarjeta, padding: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 9 }}>
              Lo que falta guardar
            </div>
            {cola.slice(0, 40).map((item) => (
              <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 9,
                padding: "7px 0", borderBottom: `1px solid ${COL.papel}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{textoDe(item)}</div>
                  <div style={{ fontSize: 10.5, color: COL.kraft, fontWeight: 600 }}>
                    {haceCuanto(item.cuando)}
                    {item.intentos > 0 && ` · ${item.intentos} ${item.intentos === 1 ? "intento" : "intentos"}`}
                  </div>
                </div>
              </div>
            ))}
            {cola.length > 40 && (
              <div style={{ fontSize: 11.5, color: COL.kraft, fontWeight: 700, paddingTop: 8 }}>
                y {cola.length - 40} más
              </div>
            )}
          </div>
        </>
      )}

      {cuantos === 0 && (
        <div style={{ ...tarjeta, padding: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <Check size={20} color={COL.billete} />
          <div style={{ fontSize: 13.5, fontWeight: 700 }}>
            No falta nada por guardar.
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Una cola de mentiras, para poder ver las pantallas antes de que exista la
// de verdad. Sale de un día real de puesto, no de números al azar.
// ------------------------------------------------------------
export const COLA_DEMO = [
  { id: "d1", motivo: "venta", monto: 89, cuando: Date.now() - 90 * 1000, intentos: 0 },
  { id: "d2", motivo: "venta", monto: 35, cuando: Date.now() - 6 * 60 * 1000, intentos: 1 },
  { id: "d3", motivo: "entrada", piezas: 10, cuando: Date.now() - 22 * 60 * 1000, intentos: 1 },
];

export const moduloSync = {
  id: "sync", nombre: "Respaldo en la nube", version: "0.1", tipo: "servicio",
  orden: 85, icono: Cloud, pantalla: null,
  probar: () => {
    // Los cinco estados, cada uno por la razón que le toca.
    const r = (o) => resumir(o).estado;
    if (r({ hayServidor: false, cola: COLA_DEMO }) !== null)
      return "sin servidor la píldora no se debe pintar";
    if (r({ cola: [] }) !== "al_dia") return "con la cola vacía debería decir al día";
    if (r({ cola: COLA_DEMO }) !== "pendiente") return "con cola y con internet, pendiente";
    if (r({ cola: COLA_DEMO, hayInternet: false }) !== "sin_internet")
      return "sin internet debería decir sin internet, no falla";
    if (r({ cola: COLA_DEMO, subiendo: true }) !== "subiendo") return "subiendo no se refleja";
    if (r({ cola: COLA_DEMO, fallas: 3 }) !== "atorado") return "tres fallas deberían ser atorado";
    if (r({ cola: COLA_DEMO, fallas: 2 }) !== "pendiente")
      return "dos fallas todavía no son atorado";
    // Con la cola vacía nada se pinta rojo, aunque antes haya fallado: si ya
    // no falta nada, el problema se resolvió solo.
    if (r({ cola: [], fallas: 9 }) !== "al_dia") return "sin cola no debería quedar atorado";

    // Que ningún estado se quede sin sus tres textos.
    for (const [nombre, E] of Object.entries(ESTADOS)) {
      for (const campo of ["corto", "titulo", "detalle"]) {
        const t = E[campo]?.(3);
        if (typeof t !== "string" || !t) return `a ${nombre} le falta ${campo}`;
        if (t !== t.toLowerCase() && t === t.toUpperCase()) return `${nombre}.${campo} va en mayúsculas`;
      }
    }

    // Los motivos del libro traducidos a palabras de puesto.
    if (!textoDe({ motivo: "venta", monto: 89 }).includes("89")) return "la venta no dice cuánto";
    if (textoDe({ motivo: "entrega" }) !== "Se lo llevó") return "entrega mal traducida";
    if (textoDe(null) !== "Algo sin identificar") return "un renglón vacío truena";

    if (haceCuanto(null) !== "nunca") return "sin fecha debería decir nunca";
    if (haceCuanto(Date.now() - 5000) !== "hace un momento") return "cinco segundos no es un momento";
    if (haceCuanto(Date.now() - 120000) !== "hace 2 minutos") return "dos minutos mal dichos";
    if (haceCuanto(Date.now() + 60000) !== "hace un momento")
      return "un reloj adelantado no debería decir 'en el futuro'";
    return true;
  },
};
